import React, {
	createContext,
	type PropsWithChildren,
	useCallback,
	useContext,
	useEffect,
	useRef,
	useState,
} from 'react';

import { usePromptOfTheDayWithFallback } from '@atlassian/rovo-prompt-of-the-day/use-prompt-of-the-day-with-fallback';

import type { PromptOfTheDay } from '../../../common/utils/quick-find/prompt-of-the-day/types';
import { useUser } from '../../store/bootstrap';
import { usePromptOfTheDayLoadAnalytics } from './use-prompt-of-the-day-load-analytics';

/** Quick Find regenerates 24 hours after the prompts were generated, not at local midnight. */
export const PROMPT_OF_THE_DAY_MAX_AGE_MS: number = 24 * 60 * 60 * 1000;

type RemovePrompt = (prompt: PromptOfTheDay) => void;

const PromptOfTheDayContext = createContext<PromptOfTheDay | undefined>(undefined);
const RemovePromptOfTheDayContext = createContext<RemovePrompt>(() => {});

/** The prompt to show as the first Quick Find row; `undefined` when there is none or no provider. */
export const usePromptOfTheDay: () => PromptOfTheDay | undefined = () =>
	useContext(PromptOfTheDayContext);

/** Removes a used prompt from the cache, so the next open shows the next one, or none. */
export const useRemovePromptOfTheDay: () => RemovePrompt = () =>
	useContext(RemovePromptOfTheDayContext);

type LoaderProps = {
	accountId: string;
	onPrompt: (prompt: PromptOfTheDay | undefined) => void;
	onRemovePrompt: (removePrompt: RemovePrompt | undefined) => void;
};

// Mounted once the user engages; the shared library owns the request, the per-account cache
// (`rovo-potd:` keys) and the one-request-per-page dedupe.
const PromptOfTheDayLoader = ({ accountId, onPrompt, onRemovePrompt }: LoaderProps): null => {
	// Yesterday's prompt stays on screen while today's loads, or if that request fails.
	const { prompts, status, removePrompt } = usePromptOfTheDayWithFallback(
		{ accountId },
		{ maxAgeMs: PROMPT_OF_THE_DAY_MAX_AGE_MS },
	);
	usePromptOfTheDayLoadAnalytics({ status, promptCount: prompts.length });

	useEffect(() => {
		onRemovePrompt(removePrompt);
	}, [onRemovePrompt, removePrompt]);

	// Unconditional, so removing the last prompt clears the row even while a refresh is loading.
	useEffect(() => {
		onPrompt(prompts[0]);
	}, [prompts, onPrompt]);

	return null;
};

type ProviderProps = PropsWithChildren<{
	/** Delay the request until the user has engaged with Quick Find. */
	isInteracted?: boolean;
	/** While open, the displayed prompt is frozen so the list never shifts under the user. */
	isOpen?: boolean;
}>;

// Mount only when the feature is on (see isPromptOfTheDayEnabled).
export const QuickFindPromptOfTheDayProvider: (props: ProviderProps) => React.JSX.Element = ({
	isInteracted = false,
	isOpen = false,
	children,
}) => {
	const [user] = useUser();
	const accountId = user?.id;

	// Once started, the loader stays mounted: closing Quick Find must not drop a pending update.
	const [hasStarted, setHasStarted] = useState(isInteracted);
	if (isInteracted && !hasStarted) {
		setHasStarted(true);
	}
	const [latest, setLatest] = useState<PromptOfTheDay | undefined>(undefined);
	const [displayed, setDisplayed] = useState<PromptOfTheDay | undefined>(undefined);

	// A ref, so the context value stays stable while the loader mounts, remounts or re-renders.
	const removePromptRef = useRef<RemovePrompt | undefined>(undefined);
	const onRemovePrompt = useCallback((removePrompt: RemovePrompt | undefined) => {
		removePromptRef.current = removePrompt;
	}, []);
	const removePrompt = useCallback<RemovePrompt>((prompt) => {
		removePromptRef.current?.(prompt);
	}, []);

	// A prompt that lands mid-open (first fetch, or today's replacing yesterday's) waits for the
	// next open: the list already on screen must not jump or change under the user.
	useEffect(() => {
		if (!isOpen) {
			setDisplayed(latest);
		}
	}, [isOpen, latest]);

	return (
		<PromptOfTheDayContext.Provider value={displayed}>
			<RemovePromptOfTheDayContext.Provider value={removePrompt}>
				{hasStarted && accountId && (
					<PromptOfTheDayLoader
						accountId={accountId}
						onPrompt={setLatest}
						onRemovePrompt={onRemovePrompt}
					/>
				)}
				{children}
			</RemovePromptOfTheDayContext.Provider>
		</PromptOfTheDayContext.Provider>
	);
};
